import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  PlusIcon, 
  PencilIcon, 
  TrashIcon,
  MagnifyingGlassIcon
} from '@heroicons/react/24/outline';
import { useApi, useFormSubmit, useAsyncOperation } from '../hooks/useApi';
import { molesAPI, portsAPI, usersAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { formatDate } from '../utils/helpers';
import Table from '../components/Table';
import Button from '../components/Button';
import Input from '../components/Input';
import Modal, { ConfirmModal } from '../components/Modal';

const MolesPage = () => {
  const { t } = useTranslation();
  const { isAdmin } = useAuth();
  const [search, setSearch] = useState('');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedMole, setSelectedMole] = useState(null);

  // API hooks
  const { 
    data: molesData, 
    loading: molesLoading, 
    refetch: refetchMoles 
  } = useApi(() => molesAPI.getAll({ search }));
  
  const { 
    data: portsData, 
    loading: portsLoading 
  } = useApi(() => portsAPI.getAll());

  const { 
    data: managersData, 
    loading: managersLoading 
  } = useApi(() => usersAPI.getAll({ limit: 100 })); // Get all users and filter client-side

  const managers = (managersData?.users || []).filter(user => 
    user.role === 'ADMIN' || user.role === 'MANAGER'
  );
  
  const accountants = (managersData?.users || []).filter(user => 
    user.role === 'ACCOUNTANT'
  );

  // Form submission hooks
  const { submit: submitCreate, loading: createLoading } = useFormSubmit(
    molesAPI.create,
    {
      onSuccess: () => {
        setIsAddModalOpen(false);
        refetchMoles();
      },
      successMessage: t('molesPage.locationCreated'),
    }
  );

  const { submit: submitUpdate, loading: updateLoading } = useFormSubmit(
    (data) => molesAPI.update(selectedMole.id, data),
    {
      onSuccess: () => {
        setIsEditModalOpen(false);
        setSelectedMole(null);
        refetchMoles();
      },
      successMessage: t('molesPage.locationUpdated'),
    }
  );

  const { execute: executeDelete, loading: deleteLoading } = useAsyncOperation();

  // Event handlers
  const handleEdit = (mole) => {
    setSelectedMole(mole);
    setIsEditModalOpen(true);
  };

  const handleDelete = (mole) => {
    setSelectedMole(mole);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!selectedMole?.id) return;
    try {
      await executeDelete(
        () => molesAPI.delete(selectedMole.id),
        { showSuccessToast: true, successMessage: t('molesPage.locationDeleted') }
      );
      setIsDeleteModalOpen(false);
      setSelectedMole(null);
      refetchMoles();
    } catch {
      /* errors handled in executeDelete */
    }
  };

  // Table columns
  const columns = [
    {
      key: 'name',
      title: t('molesPage.moleName'),
      sortable: true,
      render: (value, row) => (
        <div>
          <div className="font-medium text-gray-900">{value}</div>
          {row.description && (
            <div className="text-sm text-gray-500">{row.description}</div>
          )}
        </div>
      ),
    },

    {
      key: 'port',
      title: t('molesPage.manager'),
      render: (portValue, row) => {
        // Table passes (row[column.key], row) - for 'port' key, first param is port object, second is full row
        const mole = row;
        // Show direct mole managers first, then port managers
        const moleManagers = mole?.managers || [];
        const portManagers = mole?.port?.managers || [];
        const allManagers = [...moleManagers, ...portManagers];
        
        return allManagers.length > 0 ? (
          <div>
            {moleManagers.length > 0 && (
              <div className="mb-2">
                <div className="text-xs font-medium text-blue-600 mb-1">Direct:</div>
                {moleManagers.map((manager, index) => (
                  <div key={manager.user.id} className={index > 0 ? 'mt-1' : ''}>
                    <div className="text-sm text-gray-900">
                      {manager.user.firstName} {manager.user.lastName}
                    </div>
                    <div className="text-xs text-gray-500">@{manager.user.username}</div>
                  </div>
                ))}
              </div>
            )}
            {portManagers.length > 0 && (
              <div>
                <div className="text-xs font-medium text-gray-600 mb-1">From Port:</div>
                {portManagers.map((manager, index) => (
                  <div key={manager.user.id} className={index > 0 ? 'mt-1' : ''}>
                    <div className="text-sm text-gray-700">
                      {manager.user.firstName} {manager.user.lastName}
                    </div>
                    <div className="text-xs text-gray-500">@{manager.user.username}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <span className="text-gray-400">{t('molesPage.noManagerAssigned')}</span>
        );
      },
    },
    {
      key: 'accountants',
      title: t('molesPage.accountants', 'Accountants'),
      render: (accountantsValue, row) => {
        // Table passes (row[column.key], row) - for 'accountants' key, first param is accountants array, second is full row
        const mole = row;
        // Show direct mole accountants first, then port accountants (same structure as managers)
        const moleAccountants = mole?.accountants || [];
        const portAccountants = mole?.port?.accountants || [];
        const allAccountants = [...moleAccountants, ...portAccountants];
        
        return allAccountants.length > 0 ? (
          <div>
            {moleAccountants.length > 0 && (
              <div className="mb-2">
                <div className="text-xs font-medium text-blue-600 mb-1">Direct:</div>
                {moleAccountants.map((accountant, index) => (
                  <div key={accountant.user?.id || index} className={index > 0 ? 'mt-1' : ''}>
                    <div className="text-sm text-gray-900">
                      {accountant.user?.firstName} {accountant.user?.lastName}
                    </div>
                    <div className="text-xs text-gray-500">@{accountant.user?.username}</div>
                  </div>
                ))}
              </div>
            )}
            {portAccountants.length > 0 && (
              <div>
                <div className="text-xs font-medium text-gray-600 mb-1">From Port:</div>
                {portAccountants.map((accountant, index) => (
                  <div key={accountant.user?.id || index} className={index > 0 ? 'mt-1' : ''}>
                    <div className="text-sm text-gray-700">
                      {accountant.user?.firstName} {accountant.user?.lastName}
                    </div>
                    <div className="text-xs text-gray-500">@{accountant.user?.username}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <span className="text-gray-400">{t('molesPage.noAccountantAssigned', 'No accountant assigned')}</span>
        );
      },
    },
    {
      key: 'status',
      title: t('molesPage.status'),
      render: (value) => (
        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
          value === 'active' 
            ? 'bg-green-100 text-green-800' 
            : 'bg-gray-100 text-gray-800'
        }`}>
          {value === 'active' ? t('molesPage.active') : t('molesPage.inactive')}
        </span>
      ),
    },
    {
      key: 'weighbridges',
      title: t('molesPage.weighbridges'),
      render: (value, row) => row._count?.weighbridges || 0,
    },
    {
      key: 'loads',
      title: t('molesPage.loads'),
      render: (value, row) => row._count?.loads || 0,
    },
    {
      key: 'createdAt',
      title: t('molesPage.created'),
      sortable: true,
      render: (value) => formatDate(value),
    },
    {
      key: 'actions',
      title: t('molesPage.actions'),
      align: 'center',
      sticky: true,
      render: (_, row) => (
        <div className="flex items-center justify-center space-x-2">
          {isAdmin() && (
            <>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleEdit(row)}
                className="text-blue-600 hover:text-blue-800"
                title={t('molesPage.editLocation')}
              >
                <PencilIcon className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleDelete(row)}
                className={row._count?.loads > 0 ? "text-gray-400 cursor-not-allowed" : "text-red-600 hover:text-red-800"}
                title={row._count?.loads > 0 ? t('moles.tooltips.cannotDeleteWithLoads') : t('molesPage.deleteLocation')}
                disabled={row._count?.loads > 0}
              >
                <TrashIcon className="w-4 h-4" />
              </Button>
            </>
          )}
        </div>
      ),
    },
  ];

  const moles = molesData?.moles || [];
  const ports = portsData?.ports || [];

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">{t('molesPage.title')}</h1>
          <p className="mt-2 text-sm text-gray-700">
            {t('molesPage.subtitle')}
          </p>
        </div>
        {isAdmin() && (
          <Button
            onClick={() => setIsAddModalOpen(true)}
            icon={PlusIcon}
          >
            {t('molesPage.addMole')}
          </Button>
        )}
      </div>

      {/* Search */}
      <div className="flex items-center space-x-4">
        <div className="relative flex-1 max-w-md">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
          <Input
            type="text"
            placeholder={t('molesPage.searchPlaceholder')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {/* Table */}
      <Table
        columns={columns}
        data={moles}
        loading={molesLoading}
        emptyMessage={t('molesPage.noMolesFound')}
      />

      {/* Add Modal */}
      <MoleForm
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSubmit={submitCreate}
        loading={createLoading}
        ports={ports}
        portsLoading={portsLoading}
        managers={managers}
        managersLoading={managersLoading}
        accountants={accountants}
        title={t('molesPage.addNewLocation')}
        submitText={t('molesPage.createLocation')}
      />

      {/* Edit Modal */}
      <MoleForm
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setSelectedMole(null);
        }}
        onSubmit={submitUpdate}
        loading={updateLoading}
        ports={ports}
        portsLoading={portsLoading}
        managers={managers}
        managersLoading={managersLoading}
        accountants={accountants}
        title={t('molesPage.editLocationTitle')}
        submitText={t('molesPage.updateLocation')}
        initialData={selectedMole}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setSelectedMole(null);
        }}
        onConfirm={confirmDelete}
        loading={deleteLoading}
        title={t('molesPage.deleteMole')}
        message={`${t('molesPage.deleteConfirm')} "${selectedMole?.name}"? ${t('molesPage.deleteCannotUndo')}`}
        confirmText={t('common.delete')}
        confirmVariant="danger"
      />
    </div>
  );
};

// Mole Form Component
const MoleForm = ({
  isOpen,
  onClose,
  onSubmit,
  loading,
  ports,
  portsLoading,
  managers,
  managersLoading,
  accountants = [],
  title,
  submitText,
  initialData = null,
}) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    portId: '',
    description: '',
    managerIds: [],
    accountantIds: [],
    status: 'active',
  });

  React.useEffect(() => {
    if (isOpen) {
      if (initialData) {
        const existingManagerIds = initialData.managers?.map(m => m.user.id) || [];
        const existingAccountantIds = initialData.accountants?.map(a => a.user.id) || [];
        // Include port accountants if the mole belongs to a port
        const portAccountantIds = initialData.port?.accountants?.map(a => a.user.id) || [];
        // Combine mole accountants and port accountants, removing duplicates
        const allAccountantIds = [...new Set([...existingAccountantIds, ...portAccountantIds])];
        setFormData({
          name: initialData.name || '',
          portId: initialData.portId || '',
          description: initialData.description || '',
          managerIds: existingManagerIds,
          accountantIds: allAccountantIds,
          status: initialData.status || 'active',
        });
      } else {
        setFormData({
          name: '',
          portId: '',
          description: '',
          managerIds: [],
          accountantIds: [],
          status: 'active',
        });
      }
    }
  }, [isOpen, initialData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleManagerChange = (managerId) => {
    setFormData(prev => {
      const currentIds = prev.managerIds || [];
      const isSelected = currentIds.includes(managerId);
      
      if (isSelected) {
        // Remove manager
        return {
          ...prev,
          managerIds: currentIds.filter(id => id !== managerId)
        };
      } else {
        // Add manager
        return {
          ...prev,
          managerIds: [...currentIds, managerId]
        };
      }
    });
  };

  const handleAccountantChange = (accountantId) => {
    setFormData(prev => {
      const currentIds = prev.accountantIds || [];
      const isSelected = currentIds.includes(accountantId);
      
      if (isSelected) {
        // Remove accountant
        return {
          ...prev,
          accountantIds: currentIds.filter(id => id !== accountantId)
        };
      } else {
        // Add accountant
        return {
          ...prev,
          accountantIds: [...currentIds, accountantId]
        };
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const submitData = { 
      ...formData,
      managerIds: formData.managerIds || [],
      accountantIds: formData.accountantIds || []
    };
    if (!submitData.description) {
      delete submitData.description;
    }
    await onSubmit(submitData);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title} size="lg" closeOnOverlayClick={false}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('molesPage.form.locationNameLabel')} *
          </label>
          <Input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder={t('molesPage.form.locationNamePlaceholder')}
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('molesPage.form.portLabel')} *
          </label>
          <select
            name="portId"
            value={formData.portId}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
            required
            disabled={portsLoading}
          >
            <option value="">{t('molesPage.form.portPlaceholder')}</option>
            {ports.map((port) => (
              <option key={port.id} value={port.id}>
                {port.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('molesPage.form.descriptionLabel')}
          </label>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleChange}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
            placeholder={t('molesPage.form.descriptionPlaceholder')}
          />
        </div>

        {/* Managers */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('molesPage.form.managersLabel', 'Managers')}
          </label>
          {managersLoading ? (
            <div className="text-gray-500 text-sm">{t('common.loading', 'Loading...')}</div>
          ) : (
            <div className="space-y-2 max-h-40 overflow-y-auto border border-gray-300 rounded-md p-3">
              {managers.length === 0 ? (
                <div className="text-gray-500 text-sm">{t('molesPage.form.noManagersAvailable', 'No managers available')}</div>
              ) : (
                managers.map((manager) => (
                  <label key={manager.id} className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.managerIds?.includes(manager.id) || false}
                      onChange={() => handleManagerChange(manager.id)}
                      className="checkbox-app"
                    />
                    <span className="text-sm text-gray-700">
                      {manager.firstName} {manager.lastName} <span className="text-gray-500">(@{manager.username})</span>
                    </span>
                  </label>
                ))
              )}
            </div>
          )}
          {formData.managerIds?.length > 0 && (
            <div className="mt-2 text-sm text-gray-600">
              {t('molesPage.form.selectedManagers', 'Selected managers')}: {formData.managerIds.length}
            </div>
          )}
        </div>

        {/* Accountant Assignment */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('molesPage.form.accountantsLabel', 'Accountants')}
          </label>
          {managersLoading ? (
            <div className="text-gray-500 text-sm">{t('common.loading', 'Loading...')}</div>
          ) : (
            <div className="space-y-2 max-h-40 overflow-y-auto border border-gray-300 rounded-md p-3">
              {accountants.length === 0 ? (
                <div className="text-gray-500 text-sm">{t('molesPage.form.noAccountantsAvailable', 'No accountants available')}</div>
              ) : (
                accountants.map((accountant) => (
                  <label key={accountant.id} className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.accountantIds?.includes(accountant.id) || false}
                      onChange={() => handleAccountantChange(accountant.id)}
                      className="checkbox-app"
                    />
                    <span className="text-sm text-gray-700">
                      {accountant.firstName} {accountant.lastName} <span className="text-gray-500">(@{accountant.username})</span>
                    </span>
                  </label>
                ))
              )}
            </div>
          )}
          {formData.accountantIds?.length > 0 && (
            <div className="mt-2 text-sm text-gray-600">
              {t('molesPage.form.selectedAccountants', 'Selected accountants')}: {formData.accountantIds.length}
            </div>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('molesPage.form.statusLabel')}
          </label>
          <select
            name="status"
            value={formData.status}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="active">{t('molesPage.active')}</option>
            <option value="inactive">{t('molesPage.inactive')}</option>
          </select>
        </div>

        <div className="flex justify-end space-x-3 pt-4">
          <Button
            type="button"
            variant="secondary"
            onClick={onClose}
            disabled={loading}
          >
            {t('common.cancel')}
          </Button>
          <Button
            type="submit"
            loading={loading}
          >
            {submitText}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default MolesPage;